import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, X, Settings, Wrench, ShieldAlert } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useAuth } from "@/shared/auth/useAuth";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import { Button } from "@/shared/components/ui/button";
import { useAssistantUi } from "./assistant-ui-context";
import {
  useAssistantChat,
  useAssistantConfirm,
  useAssistantSettings,
  useAssistantUsage,
} from "./use-assistant";
import type { AssistantPendingAction, ChatMessage } from "./types";

const SUGGESTIONS = [
  "How are my campaigns doing?",
  "Which leads are pending review?",
  "What's the open rate on my launched campaigns?",
];

let messageSeq = 0;
const nextId = () => `m${++messageSeq}`;

/**
 * The assistant slide-over (ADR-0017). Global, context-aware, read-only. Shows
 * the conversation, the tools each answer ran, and the tenant's budget state.
 */
export function AssistantPanel() {
  const { open, closePanel } = useAssistantUi();
  const { user } = useAuth();
  // Only fetch settings/usage while the panel is open (it's always mounted).
  const settings = useAssistantSettings(open);
  const usage = useAssistantUsage(open);
  const chat = useAssistantChat();
  const confirm = useAssistantConfirm();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const conversationId = useRef<string | undefined>(undefined);

  // A conversation belongs to one tenant. When the active tenant changes, the
  // old conversationId is invalid there (server would 404) — start fresh so the
  // panel doesn't get wedged sending a stale id, and don't show tenant A's chat
  // under tenant B.
  const tenantId = getActiveTenantId();
  useEffect(() => {
    conversationId.current = undefined;
    setMessages([]);
  }, [tenantId]);

  const disabled = settings.data ? !settings.data.enabled || settings.data.autonomyLevel === "OFF" : false;
  const hardBlocked = usage.data ? !usage.data.budget.allowed : false;
  const canSend = input.trim().length > 0 && !chat.isPending && !disabled && !hardBlocked;

  function send(text: string) {
    const message = text.trim();
    if (!message || chat.isPending || disabled || hardBlocked) return;
    // A new turn supersedes any outstanding proposal — retire stale confirm cards
    // (the server also rejects a confirm once the proposal is no longer the last turn).
    setMessages((m) => [
      ...m.map((x) => (x.pendingAction ? { ...x, pendingAction: null } : x)),
      { id: nextId(), role: "user", content: message },
    ]);
    setInput("");
    chat.mutate(
      { message, conversationId: conversationId.current },
      {
        onSuccess: (res) => {
          conversationId.current = res.conversationId;
          setMessages((m) => [
            ...m,
            { id: nextId(), role: "assistant", content: res.reply, steps: res.steps, pendingAction: res.pendingAction },
          ]);
        },
        onError: () => {
          setMessages((m) => [
            ...m,
            { id: nextId(), role: "assistant", content: "Something went wrong — please try again." },
          ]);
        },
      },
    );
  }

  // Clear the pending flag on the proposing message, then append a follow-up turn.
  function resolvePending(msgId: string, follow: ChatMessage) {
    setMessages((m) => [
      ...m.map((x) => (x.id === msgId ? { ...x, pendingAction: null } : x)),
      follow,
    ]);
  }

  function confirmAction(msgId: string, action: AssistantPendingAction) {
    if (confirm.isPending || !conversationId.current) return;
    confirm.mutate(
      { conversationId: conversationId.current, tool: action.tool, input: action.input },
      {
        onSuccess: (res) =>
          resolvePending(msgId, { id: nextId(), role: "assistant", content: res.reply, steps: [res.step] }),
        onError: () =>
          resolvePending(msgId, { id: nextId(), role: "assistant", content: "That action couldn't be completed." }),
      },
    );
  }

  function cancelAction(msgId: string) {
    resolvePending(msgId, { id: nextId(), role: "assistant", content: "Okay — I won't make that change." });
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40" data-testid="assistant-panel">
      <div className="absolute inset-0 bg-black/40" onClick={closePanel} aria-hidden />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-ge-border-subtle bg-ge-sidebar shadow-xl">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-ge-border-subtle px-4 py-3">
          <Sparkles className="h-[16px] w-[16px] text-ge-accent" strokeWidth={2} />
          <div className="min-w-0 flex-1">
            <p className="text-[13.5px] font-semibold text-ge-primary">Assistant</p>
            <p className="text-[11px] text-ge-muted">Read-only · asks the API on your behalf</p>
          </div>
          {user?.superadmin && (
            <Link to="/assistant/settings" onClick={closePanel} title="Assistant settings" data-testid="assistant-settings-link">
              <Settings className="h-[16px] w-[16px] text-ge-secondary hover:text-ge-primary" strokeWidth={1.8} />
            </Link>
          )}
          <button type="button" onClick={closePanel} aria-label="Close" data-testid="assistant-close">
            <X className="h-[17px] w-[17px] text-ge-secondary hover:text-ge-primary" />
          </button>
        </div>

        {/* Budget / disabled banners */}
        {disabled && (
          <Banner tone="muted" testId="assistant-disabled">
            The assistant is turned off for this workspace.
          </Banner>
        )}
        {!disabled && hardBlocked && (
          <Banner tone="danger" testId="assistant-blocked">
            This month's budget has been reached. Ask an admin to raise it.
          </Banner>
        )}
        {!disabled && !hardBlocked && usage.data?.budget.warn && (
          <Banner tone="warning" testId="assistant-warn">
            Near this month's budget ({fmt(usage.data.budget.spentUsd)} of {fmt(usage.data.budget.limitUsd)}).
          </Banner>
        )}

        {/* Conversation */}
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.length === 0 && !disabled && (
            <div className="space-y-3">
              <p className="text-[13px] text-ge-secondary">Ask about your campaigns, leads, sequences or analytics. For example:</p>
              <div className="flex flex-col gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid="assistant-suggestion"
                    onClick={() => send(s)}
                    className="rounded-ge-control border border-ge-border-card px-3 py-2 text-left text-[13px] text-ge-value hover:bg-ge-card"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              m={m}
              confirming={confirm.isPending}
              onConfirm={() => m.pendingAction && confirmAction(m.id, m.pendingAction)}
              onCancel={() => cancelAction(m.id)}
            />
          ))}

          {chat.isPending && (
            <div className="text-[12px] text-ge-muted" data-testid="assistant-thinking">
              Working — reading your data…
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-ge-border-subtle px-4 py-3">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="assistant-input"
              rows={2}
              value={input}
              disabled={disabled || hardBlocked}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              placeholder={disabled ? "Assistant is off" : "Ask anything about your data…"}
              className="min-h-[44px] flex-1 resize-none rounded-ge-control border border-ge-border-card bg-transparent px-3 py-2 text-[13.5px] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none disabled:opacity-50"
            />
            <Button data-testid="assistant-send" disabled={!canSend} onClick={() => send(input)}>
              <Send className="h-[14px] w-[14px]" strokeWidth={2.2} />
            </Button>
          </div>
          {usage.data && !disabled && (
            <p className="mt-2 font-mono text-[10.5px] text-ge-faint">
              {fmt(usage.data.budget.spentUsd)} of {fmt(usage.data.budget.limitUsd)} this month · {usage.data.budget.mode.toLowerCase()} budget
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}

function MessageBubble({
  m,
  confirming,
  onConfirm,
  onCancel,
}: {
  m: ChatMessage;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const isUser = m.role === "user";
  return (
    <div className={cn("flex flex-col", isUser ? "items-end" : "items-start")} data-testid={`msg-${m.role}`}>
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap rounded-ge-control px-3 py-2 text-[13px]",
          isUser ? "bg-ge-accent-tint text-ge-primary" : "border border-ge-border-card text-ge-value",
        )}
      >
        <span>{m.content}</span>
        {m.steps && m.steps.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {m.steps.map((s, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-ge-pill border border-ge-border-card px-2 py-[2px] font-mono text-[10px] text-ge-secondary"
              >
                <Wrench className="h-[10px] w-[10px]" strokeWidth={2} />
                {s.tool}
              </span>
            ))}
          </div>
        )}
      </div>

      {m.pendingAction && (
        <div
          data-testid="assistant-confirm-card"
          className="mt-2 w-[85%] rounded-ge-control border border-ge-warning/40 bg-ge-warning/5 px-3 py-3"
        >
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-[1px] h-[15px] w-[15px] shrink-0 text-ge-warning" strokeWidth={2} />
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-medium text-ge-primary">Confirm this change</p>
              <p className="mt-[2px] text-[12px] text-ge-secondary">{m.pendingAction.label}</p>
              <div className="mt-2.5 flex gap-2">
                <Button size="sm" data-testid="assistant-confirm" disabled={confirming} onClick={onConfirm}>
                  {confirming ? "Applying…" : "Confirm"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="assistant-cancel"
                  disabled={confirming}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Banner({
  children,
  tone,
  testId,
}: {
  children: React.ReactNode;
  tone: "muted" | "warning" | "danger";
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "px-4 py-2 text-[12px]",
        tone === "danger"
          ? "bg-ge-danger/10 text-ge-danger"
          : tone === "warning"
            ? "bg-ge-warning/10 text-ge-warning"
            : "bg-ge-card text-ge-secondary",
      )}
    >
      {children}
    </div>
  );
}

function fmt(usd: number): string {
  return `$${usd.toFixed(2)}`;
}
